import React from 'react';
import { Grid, Box, Typography, CircularProgress, Alert } from '@mui/material';
import { Description as TemplateIcon } from '@mui/icons-material';
import TemplateCard from './TemplateCard';
import { SEARCH_MIN_CHARS } from '../../../../config/constants';

/**
 * TemplateGrid Component
 *
 * Displays the grid of template cards with loading and empty states.
 *
 * @param {Object} props
 * @param {Array} props.templates - Array of template objects
 * @param {boolean} props.loading - Whether data is loading
 * @param {boolean} props.hasAnyTemplates - Whether any templates exist
 * @param {boolean} props.loadedOnce - Whether data has been loaded at least once
 * @param {string} props.searchQuery - Current search query
 * @param {string} props.categoryFilter - Current category filter
 * @param {Object} props.selectedTemplate - Currently selected template
 * @param {Map} props.templatesWithActiveExperiments - Map of template IDs to active experiments
 * @param {Function} props.onTemplateSelect - Callback when template is selected
 */
function TemplateGrid({
  templates,
  loading,
  hasAnyTemplates,
  loadedOnce,
  searchQuery,
  categoryFilter,
  selectedTemplate,
  templatesWithActiveExperiments,
  onTemplateSelect,
}) {
  const trimmedQuery = searchQuery.trim();
  const isLoading = !hasAnyTemplates && !loadedOnce && loading;
  const showMinCharsMessage =
    trimmedQuery.length > 0 && trimmedQuery.length < SEARCH_MIN_CHARS;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (templates.length === 0) {
    return (
      <Grid container spacing={2.5} sx={{ mt: 2.5 }}>
        <Grid size={{ xs: 12 }}>
          <Box
            textAlign="center"
            py={6}
            sx={{
              backgroundColor: 'action.hover',
              borderRadius: 2,
              border: '2px dashed',
              borderColor: 'divider',
            }}
          >
            <TemplateIcon
              sx={{
                fontSize: 72,
                color: 'text.disabled',
                mb: 2,
                opacity: 0.5,
              }}
            />
            <Typography
              variant="h6"
              color="text.secondary"
              gutterBottom
              fontWeight={600}
            >
              No templates found
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 400, mx: 'auto', mt: 1 }}
            >
              {trimmedQuery.length >= SEARCH_MIN_CHARS ||
              categoryFilter !== 'all'
                ? 'Try adjusting your search or filter criteria to find templates.'
                : trimmedQuery.length > 0
                  ? `Please enter at least ${SEARCH_MIN_CHARS} characters to search.`
                  : 'No templates are available for creating experiments. Create a template first or ensure at least one template is in Active or Draft status.'}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      {showMinCharsMessage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Please enter at least {SEARCH_MIN_CHARS} characters to search.
        </Alert>
      )}
      <Grid container spacing={2.5} sx={{ mt: 2.5 }}>
        {templates.map(template => {
          const hasActiveExperiment = templatesWithActiveExperiments.has(
            template.id
          );
          const activeExperiment = hasActiveExperiment
            ? templatesWithActiveExperiments.get(template.id)
            : null;

          return (
            <Grid
              size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}
              key={template.id}
            >
              <TemplateCard
                template={template}
                isSelected={selectedTemplate?.id === template.id}
                hasActiveExperiment={hasActiveExperiment}
                activeExperiment={activeExperiment}
                onSelect={onTemplateSelect}
              />
            </Grid>
          );
        })}
      </Grid>
    </>
  );
}

export default TemplateGrid;
